import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { ReferenceBasketVersion } from '~/Api/HrPriceReferencing/MaxPrice';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { ErrorResponse, HrMaxPrice as ApiHrMaxPrice, Product, Update } from '~/Api/User/ProductGroup/HrMaxPrice';
import { handleError } from '~/ErrorHandler';
import { logGoogleTagManagerGroupProductsRemove } from '~/GoogleTagManager';
import { removeFromGroup } from '~/GroupProduct';
import {
	Action as ReferenceBasketVersionsAction,
	fetchReferenceBasketVersions,
	Module,
	pruneReferenceBasketVersions,
	selectReferenceBasketVersions,
	State as PriceReferencingState,
} from '~/PriceReferencing';
import { PricedProduct, Product as GeneralProduct } from '~/Product';
import { Action as ProductDatasetAction, pruneDatasets } from '~/ProductDataset';
import { Action as ProductPriceAction, pruneAllPrices } from '~/ProductPrice';
import { ThunkAction, ThunkDispatch } from '~/Store';

import { Action } from './Action';
import {
	commitHrMaxPriceUpdate,
	fetchHrMaxPrice,
	fetchReferenceProductTypes,
	handleCommitError,
	handleHrMaxPriceUpdateResponse,
	pruneHrMaxPrice,
	scheduleHrMaxPriceUpdate,
} from './actions';
import HrMaxPriceContent from './HrMaxPrice';
import { selectExport, selectHrMaxPrice, State as HrMaxPriceState } from './reducer';

interface BasketProduct {
	country: Country;
	product: Product;
	usedForCalculation: boolean;
}
interface BasketOptionalProduct {
	country: Country;
	product: Product | null;
	usedForCalculation: boolean;
}

const containsCountry = (countries: Country[], code: string): boolean =>
	countries.find((country) => country.alpha2 === code) !== undefined;

const compareReferences = (
	reference: BasketProduct | BasketOptionalProduct,
	otherReference: BasketProduct | BasketOptionalProduct
): number => {
	if (reference.product !== null && otherReference.product === null) {
		return -1;
	}
	if (reference.product === null && otherReference.product !== null) {
		return 1;
	}
	const comparison = reference.country.alpha2.localeCompare(otherReference.country.alpha2);
	if (comparison !== 0) {
		return comparison;
	}
	if (reference.product !== null && otherReference.product !== null) {
		return reference.product.product.brandName.localeCompare(otherReference.product.product.brandName);
	}
	return 0;
};

const groupProducts = memoizeOne((hrMaxPrice: ApiHrMaxPrice | null, versions: ReferenceBasketVersion[] | null) => {
	const version =
		versions !== null && hrMaxPrice !== null
			? versions.find((version) => version.id === hrMaxPrice.reference.referenceBasketVersion) ?? null
			: null;
	const referenceCountries = version !== null ? version.referenceCountries : [];
	const otherEuEeaCountries = version !== null ? version.otherEuEeaCountries : [];
	const products = hrMaxPrice !== null && version !== null ? hrMaxPrice.products : [];
	const nonsetCountries = new Set([
		...(referenceCountries ?? []).map((country) => country.alpha2),
		...(otherEuEeaCountries ?? []).map((country) => country.alpha2),
	]);
	const usedForCalculationIds = new Set(
		hrMaxPrice?.finalCalculation?.comparativePrice?.fromProducts?.map((product) => product.id)
	);

	const croatianProducts: BasketOptionalProduct[] = [];
	const referencedProducts: BasketOptionalProduct[] = [];
	const otherEuEeaProducts: BasketOptionalProduct[] = [];
	const otherProducts: BasketProduct[] = [];

	products.forEach((product) => {
		const { country } = product.product.dataset.source;
		if (country.alpha2 === 'HR') {
			croatianProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			return;
		}
		if (containsCountry(referenceCountries ?? [], country.alpha2)) {
			referencedProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			nonsetCountries.delete(country.alpha2);
			return;
		}
		if (containsCountry(otherEuEeaCountries ?? [], country.alpha2)) {
			otherEuEeaProducts.push({
				country,
				product,
				usedForCalculation: usedForCalculationIds.has(product.product.id),
			});
			nonsetCountries.delete(country.alpha2);
			return;
		}
		otherProducts.push({
			country,
			product,
			usedForCalculation: usedForCalculationIds.has(product.product.id),
		});
	});

	if (croatianProducts.length === 0) {
		croatianProducts.push({
			country: new Country('HR', 'Croatian Republic'),
			product: null,
			usedForCalculation: false,
		});
	}

	referenceCountries?.forEach((country) => {
		if (nonsetCountries.has(country.alpha2)) {
			referencedProducts.push({
				country,
				product: null,
				usedForCalculation: false,
			});
		}
	});

	if (otherEuEeaProducts.length > 0) {
		otherEuEeaCountries?.forEach((country) => {
			if (nonsetCountries.has(country.alpha2)) {
				otherEuEeaProducts.push({
					country,
					product: null,
					usedForCalculation: false,
				});
			}
		});
	}

	croatianProducts.sort(compareReferences);
	referencedProducts.sort(compareReferences);
	otherEuEeaProducts.sort(compareReferences);
	otherProducts.sort(compareReferences);

	const basketProducts = [...croatianProducts, ...referencedProducts, ...otherEuEeaProducts, ...otherProducts].filter(
		(product) => product.product !== null
	) as BasketProduct[];
	const pricedProducts = basketProducts.map(
		(product) => new PricedProduct(product.product, product.product.product, null)
	);

	return {
		croatianProducts,
		otherEuEeaProducts,
		otherProducts,
		pricedProducts,
		referenceBasketVersion: version,
		referencedProducts,
	};
});

interface State {
	hrMaxPrice: HrMaxPriceState;
	priceReferencing: PriceReferencingState;
}

const mapStateToProps = (state: State, { group }: { group: UserProductGroup }) => {
	const hrMaxPriceState = selectHrMaxPrice(state.hrMaxPrice, group.id);
	const hrMaxPrice = hrMaxPriceState?.hrMaxPrice ?? null;
	const versions: ReferenceBasketVersion[] | null = selectReferenceBasketVersions(
		state.priceReferencing,
		Module.hrMaxPrice
	);

	return {
		...groupProducts(hrMaxPrice, versions),
		hrMaxPrice,
		isFetching: Boolean(hrMaxPriceState?.isFetching),
		validationResult: selectExport(state.hrMaxPrice, group.id)?.validationResult ?? null,
		versions,
	};
};

type PruneAction = Action | ProductPriceAction | ProductDatasetAction | ReferenceBasketVersionsAction;

const actions = {
	onFetch:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			dispatch(fetchReferenceBasketVersions(Module.hrMaxPrice));
			dispatch(fetchReferenceProductTypes());

			try {
				await dispatch(commitHrMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}

			try {
				await dispatch(fetchHrMaxPrice(group.id));
			} catch (error) {
				if (!(error instanceof ErrorResponse)) {
					dispatch(handleError(error));
					return;
				}
				error.handleProductGroupNotFound();
				dispatch(handleError(error));
			}
		},
	onPrune:
		(group: UserProductGroup): ThunkAction<Promise<void>, PruneAction> =>
		async (dispatch) => {
			try {
				await dispatch(commitHrMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleCommitError(error));
			}
			dispatch(pruneHrMaxPrice(group.id));
			dispatch(pruneReferenceBasketVersions());
			dispatch(pruneAllPrices());
			dispatch(pruneDatasets());
		},
	onSave:
		(group: UserProductGroup): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(commitHrMaxPriceUpdate(group.id));
			} catch (error) {
				dispatch(handleHrMaxPriceUpdateResponse(error, group.id));
			}
		},
	onUpdate:
		(group: UserProductGroup, update: Update): ThunkAction<Promise<void>, Action> =>
		async (dispatch) => {
			try {
				await dispatch(scheduleHrMaxPriceUpdate(group.id, update));
			} catch (error) {
				dispatch(handleHrMaxPriceUpdateResponse(error, group.id));
			}
		},
	onDeleteItems:
		(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void) =>
		async (dispatch: ThunkDispatch<AnyAction>) => {
			try {
				await dispatch(removeFromGroup(group.id, products));
				logGoogleTagManagerGroupProductsRemove(group.id, products);
				if (onSuccess !== undefined) {
					onSuccess();
				}
			} catch (error) {
				dispatch(handleError(error));
			}
		},
};

export default connect(mapStateToProps, actions)(HrMaxPriceContent);
